<template>
  <div class="PreviewPublishToolbar">
    <div class="PreviewPublishToolbar__start">
      <UIButton
        class="PreviewPublishToolbar__close"
        leadIcon="close"
        theme="tertiary"
        size="sm"
        isSquare
        @click="previewStore.closePreview()"
      />

      <PreviewHtmlSelector
        v-if="showHtmlSelector"
        class="PreviewPublishToolbar__html-selector"
        :blocks="selectorBlocks"
        :selectedKey="selectedBlockKey"
        :isDisabled="isSelectingBlock"
        @select="onSelectHtmlBlock"
      />
      <span v-else class="PreviewPublishToolbar__website-name">
        {{ websiteDisplayName }}
      </span>
    </div>

    <div class="PreviewPublishToolbar__end">
      <WebsitePublishedActions
        v-if="hasManagedPublishedWebsite"
        :websiteId="effectiveWebsiteId"
        :publishedHref="publishedShareHref"
        :htmlContent="htmlContent"
        :downloadFileName="downloadFileName"
        @edit="onEditSettings"
        @unpublished="onWebsiteUnpublished"
        @deleted="onWebsiteDeleted"
      />
      <UIButton
        v-else-if="!isHtmlGenerating && !isHtmlIncomplete && !isLiveDraft"
        size="sm"
        class="PreviewPublishToolbar__publish"
        :isDisabled="!canPublish"
        :isLoading="isPublishing"
        @click="onPublishClick"
      >
        Publish to Web
      </UIButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, ref, watch } from 'vue'

import { UIButton } from '@foundever/ui-lib'

import { useHtmlBlockPublishSync } from '@/composables/useHtmlBlockPublishSync'
import {
  useHtmlPreviewPublish,
  type ChatHtmlPreviewBlock,
} from '@/composables/useHtmlPreviewPublish'
import { useChatStore } from '@/store/chat'
import {
  countClosedHtmlFences,
  isGeneratedAssetIncomplete,
  isHtmlBlockGenerating,
  usePreviewStore,
} from '@/store/preview'
import { useToast } from '@/store/toast'
import {
  extractHtmlPageTitle,
  slugifyWebsiteName,
  websitePublicHref,
} from '@/utils/websiteSlug'
import PreviewHtmlSelector from '@/views/components/preview/PreviewHtmlSelector.vue'
import { websitePublishKey } from '@/views/components/preview/websitePublishKey'
import WebsitePublishedActions from '@/views/components/websites/WebsitePublishedActions.vue'

const emit = defineEmits<{ publish: [] }>()

const previewStore = usePreviewStore()
const chatStore = useChatStore()
const { syncBlockUnpublished, syncBlockDeleted } = useHtmlBlockPublishSync()
const websitePublish = inject(websitePublishKey)
if (!websitePublish) {
  throw new Error('websitePublishKey is not provided')
}
const { isPublishing } = websitePublish

// The chat's single website (one website per chat) comes from GET /chats/{id}/website —
// resolved chat-wide by the backend, not per block, and fresh after every publish.
const {
  websiteSlug,
  websiteName,
  htmlContent,
  effectiveWebsiteId,
  chatHtmlBlocks,
  chatWebsite,
  chatWebsiteSummary,
  selectedBlockIsLive,
} = useHtmlPreviewPublish()

// Show the HTML selector only when the chat has more than one generated HTML;
// otherwise the toolbar shows the single page's name as plain text.
const showHtmlSelector = computed(() => chatHtmlBlocks.value.length > 1)

// Selector shows "Published {date}". Prefer the backend descriptor's publish date
// (EVGPT-1565) — it's authoritative and also covers pages whose message isn't
// loaded. Fall back to the chat website's `published_at` (EVGPT-1592) only for a
// just-(re)published block the descriptor snapshot hasn't caught up with (the date
// isn't in per-block file.metadata). `published_at` is the publish timestamp itself,
// so unlike `updated_at` a later rename or visibility edit can't skew it.
const selectorBlocks = computed<ChatHtmlPreviewBlock[]>(() =>
  chatHtmlBlocks.value.map((block) => {
    if (block.publishedAt != null) return block
    const isChatWebsite =
      Boolean(block.websiteId) &&
      block.websiteId === chatWebsite.value?.websiteId
    return {
      ...block,
      publishedAt:
        isChatWebsite && block.isPublished
          ? (chatWebsiteSummary.value?.published_at ?? undefined)
          : undefined,
    }
  }),
)

// `blockIndex` is per-message, so identify the previewed block by message + index.
// Fall back to matching the previewed raw HTML when the content has no publish key.
const selectedBlockKey = computed(() => {
  const content = previewStore.content
  if (content?.type !== 'html') return ''
  const key = content.htmlPublishKey
  if (key) return `${key.messageId}:${key.blockIndex}`
  const raw = content.raw?.trim()
  const match = chatHtmlBlocks.value.find((block) => block.raw === raw)
  return match ? `${match.messageId}:${match.blockIndex}` : ''
})

// Show the managed "published" actions (open live / unpublish / delete) only when
// the previewed block is the chat website's live page. When a different block is
// selected, the Publish button stays available so publishing overwrites the one
// chat page with the selected HTML (single page, last published wins).
const hasManagedPublishedWebsite = computed(
  () => selectedBlockIsLive.value && effectiveWebsiteId.value != null,
)

const canPublish = computed(() => Boolean(htmlContent.value?.trim()))

// The HTML agent's page as written so far: the finished page replaces it before it can go out.
const isLiveDraft = computed(
  () =>
    previewStore.content?.type === 'html' && !!previewStore.content.isLiveDraft,
)

// Mirrors the asset card. The previewed block's locator resolves its source message,
// whose closed-fence count says whether the model finished emitting it; failing that,
// whether the chat is streaming at all. With no publish key (preview opened by raw
// match) only the chat-level signal applies, keyed on the active chat — the panel
// always belongs to it.
const previewedBlockSignals = computed(() => {
  const content = previewStore.content
  const key = content?.type === 'html' ? content.htmlPublishKey : undefined
  const sourceMessage = key
    ? chatStore.getChatMessage(key.chatId, key.messageId)
    : undefined
  return {
    kind: 'html' as const,
    raw: htmlContent.value,
    hasSourceMessage: key != null && sourceMessage != null,
    isFenceClosed:
      key != null &&
      sourceMessage != null &&
      countClosedHtmlFences(sourceMessage.content) > key.blockIndex,
    isChatResponding:
      chatStore.chatIsRespondingMap[key?.chatId || chatStore.activeChatId] ||
      false,
  }
})

// A fragment with no `</html>` settles once its fence closes or the chat stops
// streaming, so the Publish button is never hidden forever.
const isHtmlGenerating = computed(() =>
  isHtmlBlockGenerating(previewedBlockSignals.value),
)

// A page that stopped before the model finished it (interrupted, stopped, or cut at
// the token limit) can't be published — same rule as the asset card's menu. Only
// decidable from the source message: opened by raw match, an unknown fence state must
// not read as unclosed, or a finished fragment would lose its Publish button.
const isHtmlIncomplete = computed(
  () =>
    previewedBlockSignals.value.hasSourceMessage &&
    isGeneratedAssetIncomplete(previewedBlockSignals.value),
)

const publishedShareHref = computed(() => {
  const website = chatWebsiteSummary.value
  if (website?.link) return websitePublicHref(website.link)
  const slug = websiteSlug.value.trim()
  if (slug && chatWebsite.value?.isPublished) {
    return websitePublicHref(`/w/${slug}`)
  }
  return ''
})

const downloadFileName = computed(
  () => chatWebsiteSummary.value?.name ?? 'webpage.html',
)

const websiteDisplayName = computed(() => {
  const website = chatWebsiteSummary.value
  if (website?.name) return website.name
  // Single-HTML chat with no website yet: show the generated page's title.
  const onlyBlock = chatHtmlBlocks.value[0]
  if (onlyBlock?.title) return onlyBlock.title
  return websiteName.value || 'Untitled website'
})

function onWebsiteUnpublished() {
  const content = previewStore.content
  if (content?.type === 'html' && content.htmlPublishKey) {
    syncBlockUnpublished(content.htmlPublishKey, content.websiteId ?? null)
  }
  previewStore.updateHtmlPreview({ isWebsitePublished: false })
}

function onWebsiteDeleted() {
  const content = previewStore.content
  if (content?.type === 'html' && content.htmlPublishKey) {
    syncBlockDeleted(content.htmlPublishKey)
  }
  previewStore.updateHtmlPreview({
    websiteId: undefined,
    isWebsitePublished: false,
    publishedHtmlApiUrl: null,
  })
  previewStore.closePreview()
}

// True while fetching a selected page's message (see below); disables the selector.
const isSelectingBlock = ref(false)

// Switch which generated HTML the preview shows. Seeds the linked website from the
// block's `file.metadata` (if it is the live one) so publish state resolves; the
// chat website (one slug per chat) is resolved chat-wide in useHtmlPreviewPublish.
async function onSelectHtmlBlock(block: ChatHtmlPreviewBlock) {
  let raw = block.raw
  if (raw == null) {
    // Descriptor-only page: its message is outside the loaded window, so we don't
    // have the HTML yet. Fetch that one message (GET /chats/{id}/messages/{id}), then
    // re-read the block — now scanned with `raw` — from chatHtmlBlocks.
    isSelectingBlock.value = true
    try {
      await chatStore.ensureMessageLoaded(block.chatId, block.messageId)
    } finally {
      isSelectingBlock.value = false
    }
    raw = chatHtmlBlocks.value.find(
      (b) =>
        b.messageId === block.messageId && b.blockIndex === block.blockIndex,
    )?.raw
  }
  if (raw == null) {
    useToast().onError('Could not load this page. Please try again.')
    return
  }
  previewStore.openPreview({
    type: 'html',
    raw: raw.trim(),
    htmlPublishKey: {
      chatId: block.chatId,
      messageId: block.messageId,
      blockIndex: block.blockIndex,
    },
    ...(block.websiteId
      ? { websiteId: block.websiteId, isWebsitePublished: block.isPublished }
      : {}),
  })
}

// "Edit settings" on the published page: open the same publish modal, which resolves
// update mode from the live website and pre-fills its current settings.
function onEditSettings() {
  if (isPublishing.value) return
  emit('publish')
}

function onPublishClick() {
  if (!canPublish.value || isPublishing.value) return
  // Always open the publish modal so the user can review and change the publication
  // type (visibility / password / shared users) before (re)publishing — including
  // re-publishing a previously unpublished page on the same slug. The modal resolves
  // update-vs-create from the linked websiteId and pre-fills the current settings.
  emit('publish')
}

// Fresh chat with no website yet: propose a default slug/name from the selected
// HTML's title so the publish modal pre-fills. Skipped once the chat has a website
// (one slug per chat) — the modal then reuses the existing website's slug/name.
watch(
  () => previewStore.content,
  (content) => {
    if (content?.type !== 'html') return
    if (
      !content.websiteSlug &&
      !content.websiteId &&
      !effectiveWebsiteId.value
    ) {
      const title = extractHtmlPageTitle(content.raw)
      previewStore.updateHtmlPreview({
        websiteSlug: slugifyWebsiteName(title),
        websiteName: content.websiteName ?? title,
      })
    }
  },
  { immediate: true },
)

// Seed the previewed content with the chat website's slug/name/description once
// fetched (drives the publish modal's prefill + the share href). Publish state is
// read per-block from `file.metadata`, so this deliberately does not set
// `isWebsitePublished` — a non-live selected block must not read as published.
watch(
  chatWebsiteSummary,
  (website) => {
    if (!website) return
    previewStore.updateHtmlPreview({
      websiteId: website.id,
      websiteSlug: website.slug,
      websiteName: website.name,
      websiteOwnedSlug: website.slug,
      websiteDescription: website.description ?? '',
    })
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
.PreviewPublishToolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  min-height: 3rem;
  padding: 0.75rem;
  border-bottom: 1px solid var(--border-default);
  background: $color-white;
  flex-shrink: 0;

  .PreviewPublishToolbar__start {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    min-width: 0;
  }

  .PreviewPublishToolbar__close {
    flex-shrink: 0;
    border-radius: 0.5rem;
  }

  .PreviewPublishToolbar__html-selector {
    min-width: 0;
  }

  .PreviewPublishToolbar__website-name {
    @include font-size-14px;
    font-weight: 500;
    line-height: 1.125rem;
    color: $color-neutral-170;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .PreviewPublishToolbar__end {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  .PreviewPublishToolbar__publish {
    flex-shrink: 0;
  }
}
</style>
